/**
 * Product Footer Component - Compact Professional Design
 *
 * Clean footer with all information visible, no expandable sections.
 */

import { Link } from 'react-router-dom'
import {
  ExternalLink,
  FileCheck,
  Building2,
  Mail,
  Phone,
  MapPin,
  Code2,
} from 'lucide-react'
import { t } from '@/i18n'
import { config } from '@/config'
import { useBusinessInfo } from '@/hooks/useBusinessInfo'
import { Logo } from '@/components/ui/Logo'
import { cn } from '@/lib/utils'

interface ProductFooterProps {
  className?: string
  showBusiness?: boolean
}

export function ProductFooter({
  className = '',
  showBusiness = true,
}: ProductFooterProps) {
  const currentYear = new Date().getFullYear()
  const { data: business } = useBusinessInfo()

  return (
    <footer className={cn(
      'border-t bg-card/50 backdrop-blur-sm',
      className
    )}>
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-5">
        {/* Mobile Footer */}
        <div className="md:hidden space-y-3">
          {/* Logo and Hacienda */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Logo size="sm" />
              <div>
                <div className="font-bold text-sm">
                  <span className="text-foreground/60">Invo</span>
                  <span className="text-amber-500/80 dark:text-amber-300/80">Sloth</span>
                </div>
                <span className="text-[10px] text-muted-foreground">v{config.product.version}</span>
              </div>
            </Link>
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded text-xs">
              <FileCheck className="h-3 w-3" />
              {t('footer.haciendaVersion')}
            </div>
          </div>

          {/* Business Info - Mobile */}
          {showBusiness && business?.name && (
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <div className="flex items-center gap-2 font-medium mb-1">
                <Building2 className="h-4 w-4 text-primary" />
                {business.name}
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {business.identification && <p>{t('footer.identification')}: {business.identification}</p>}
                {business.phone && (
                  <a href={`tel:${business.phone}`} className="flex items-center gap-1 hover:text-primary">
                    <Phone className="h-3 w-3" />
                    {business.phone}
                  </a>
                )}
                {business.email && (
                  <a href={`mailto:${business.email}`} className="flex items-center gap-1 hover:text-primary">
                    <Mail className="h-3 w-3" />
                    {business.email}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Creator Credit - Mobile */}
          <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border/50">
            <p>{t('footer.developedBy')}{' '}
              <a
                href={config.creator.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-0.5"
              >
                {config.creator.name}
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </p>
            <p className="mt-1">&copy; {currentYear} {t('footer.copyright')}</p>
          </div>
        </div>

        {/* Desktop Footer */}
        <div className="hidden md:block">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            {/* Product Info */}
            <div className="space-y-3">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit">
                <Logo size="md" variant="glow" />
                <div>
                  <div className="font-bold text-lg">
                    <span className="text-foreground/60">Invo</span>
                    <span className="text-amber-500/80 dark:text-amber-300/80">Sloth</span>
                  </div>
                  <span className="text-xs text-muted-foreground">v{config.product.version}</span>
                </div>
              </Link>
              <p className="text-sm text-muted-foreground">
                {config.product.description}
              </p>
            </div>

            {/* Business Info */}
            {showBusiness && business?.name && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  {t('footer.business')}
                </h4>
                <div className="space-y-1.5 text-sm">
                  <p className="font-medium">{business.name}</p>
                  {business.identification && (
                    <p className="text-muted-foreground text-xs">
                      {t('footer.identification')}: {business.identification}
                    </p>
                  )}
                  {business.address && (
                    <p className="text-muted-foreground text-xs flex items-start gap-1">
                      <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                      {business.address}
                    </p>
                  )}
                  {business.email && (
                    <a
                      href={`mailto:${business.email}`}
                      className="text-muted-foreground text-xs flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      <Mail className="h-3 w-3" />
                      {business.email}
                    </a>
                  )}
                  {business.phone && (
                    <a
                      href={`tel:${business.phone}`}
                      className="text-muted-foreground text-xs flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      <Phone className="h-3 w-3" />
                      {business.phone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Hacienda Badge */}
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-emerald-500" />
                {t('footer.electronicInvoicing')}
              </h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded text-xs font-medium">
                    {t('footer.haciendaVersion')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('footer.haciendaIntegrationDesc')} {config.hacienda.country}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('footer.haciendaFeatures')}
                </p>
              </div>
            </div>

            {/* Creator Info */}
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Code2 className="h-4 w-4 text-primary" />
                {t('footer.developedBy')}
              </h4>
              <div className="space-y-1.5 text-sm">
                <a
                  href={config.creator.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:underline inline-flex items-center gap-1"
                >
                  {config.creator.name}
                  <ExternalLink className="h-3 w-3" />
                </a>
                <p className="text-xs text-muted-foreground">
                  {t('footer.enterpriseSolutions')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {config.hacienda.country}
                </p>
              </div>
            </div>
          </div>

          {/* Copyright Line */}
          <div className="mt-5 pt-4 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>&copy; {currentYear} {config.creator.name}. {t('footer.copyright')}</p>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <FileCheck className="h-3 w-3 text-emerald-500" />
                {t('footer.haciendaVersion')}
              </span>
              <span className="text-border">|</span>
              <span>{config.hacienda.country}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

/**
 * Compact Footer for Dashboard
 */
export function CompactFooter({ className = '' }: { className?: string }) {
  const currentYear = new Date().getFullYear()
  const { data: business } = useBusinessInfo()

  return (
    <footer className={cn(
      'text-center py-3 text-xs text-muted-foreground border-t',
      className
    )}>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <span className="font-semibold">
          <span className="text-foreground/60">Invo</span>
          <span className="text-amber-500/80 dark:text-amber-300/80">Sloth</span>
        </span>
        <span>v{config.product.version}</span>
        <span className="text-border">|</span>
        <span className="flex items-center gap-1">
          <FileCheck className="h-3 w-3 text-emerald-500" />
          {t('footer.haciendaVersion')}
        </span>
        {business?.name && (
          <>
            <span className="hidden sm:inline text-border">|</span>
            <span className="hidden sm:flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {business.name}
            </span>
          </>
        )}
        <span className="hidden md:inline text-border">|</span>
        <a
          href={config.creator.website}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex hover:text-primary transition-colors items-center gap-1"
        >
          {config.creator.name}
          <ExternalLink className="h-3 w-3" />
        </a>
        <span className="hidden lg:inline text-border">|</span>
        <span className="hidden lg:inline">&copy; {currentYear}</span>
      </div>
    </footer>
  )
}
